/**
 * Picks the 3D rendering image for a product, or for a plate cut to a shape, from the renderings
 * folder on the static-assets CDN (`NEXT_PUBLIC_URI_STATIC`, baked in at build time).
 *
 * Position: used by the define-cut modal (`components/Shared/DefineCutModal/DefineCutModal.tsx`)
 * and the catalog guided view (`CatalogProductsGuidedViewDesktop.tsx` / `Mobile.tsx`). Every file
 * name returned must exist under `/static-assets/images/renderings/3d-renderings/` on that CDN,
 * which is not part of this repository.
 */
import {
	Attribute,
	LanguageToLocale,
	MatchingProduct,
	Product,
	ProductAttribute,
} from "@ryerson/frontend.common";
import { CutShape } from "@ryerson/common.process-cut";
import { getAttributeName } from "@utils/translateAttributes";

/** Base URL of the 3D renderings folder, with a trailing slash. */
export const imagePath =
	(process.env.NEXT_PUBLIC_URI_STATIC ?? "") + "/static-assets/images/renderings/3d-renderings/";
/** The sheet rendering, used when no form or shape matches. */
export const defaultImage = `${imagePath}Sheet.svg`;

/**
 * Returns the rendering URL for a product. A plate with a `cutShape` gets the plate-cut image for
 * that shape (circle, rectangle, ring, square). Otherwise the product's form (non-letters replaced
 * by spaces, lower-cased) and shape pick the image; for structural angles, beams and channels the
 * metal category and the product's localized "Type" attribute refine it. Unknown values fall back
 * to the form's generic image, then to {@link defaultImage}.
 */
export const getProduct3DRenderImage = (
	product: Product | MatchingProduct = {} as Product,
	cutShape: CutShape = "" as CutShape,
	locale: LanguageToLocale
) => {
	const { category, form, shape } = product;
	const attributes: (ProductAttribute | Attribute)[] = product.attributes;
	const productType = attributes?.find((attribute) => {
		const attributeName = getAttributeName(attribute, locale);
		return attributeName?.toLowerCase() === "type";
	});
	const formType = form?.replace(/[^a-zA-Z\s]/g, " ")?.toLowerCase();

	if (cutShape && formType === "plate") {
		switch (cutShape?.toLowerCase()) {
			case "circle":
				return `${imagePath}PlateCircle.svg`;
			case "rectangle":
				return `${imagePath}PlateRectangle.svg`;
			case "ring":
				return `${imagePath}PlateRing.svg`;
			case "square":
				return `${imagePath}PlateSquare.svg`;
			default:
				return defaultImage;
		}
	} else {
		switch (formType) {
			case "bar":
				switch (shape?.toLowerCase()) {
					case "flat":
						return `${imagePath}BarFlat.svg`;
					case "half oval":
						return `${imagePath}BarHalfOval.svg`;
					case "half round":
						return `${imagePath}BarHalfRound.svg`;
					case "hexagon":
						return `${imagePath}BarHexagon.svg`;
					case "octagon":
						return `${imagePath}BarOctagon.svg`;
					case "reinforcing":
						return `${imagePath}BarRoundReinforced.svg`;
					case "round":
						return `${imagePath}BarRound.svg`;
					case "square":
						return `${imagePath}BarSquare.svg`;
					case "tee":
						return `${imagePath}BarTee.svg`;
					default:
						return `${imagePath}BarFlat.svg`;
				}
			case "coil":
				if (shape?.toLowerCase() === "tread") {
					return `${imagePath}CoilTread.svg`;
				} else {
					return `${imagePath}Coil.svg`;
				}
			case "expanded metal":
				return `${imagePath}ExpandedSheet.svg`;
			case "expanded standard":
				return `${imagePath}ExpandedSheet.svg`;
			case "flooring":
				return `${imagePath}FlooringOpenGrip.svg`;
			case "pipe":
				return `${imagePath}PipeRound.svg`;
			case "plate":
				return `${imagePath}PlateRectangle.svg`;
			case "sheet":
				return `${imagePath}Sheet.svg`;
			case "structural":
				switch (shape?.toLowerCase()) {
					case "angle":
						switch (category?.toLowerCase()) {
							case "aluminum":
								if (productType?.value?.toLowerCase() === "alum assoc") {
									return `${imagePath}AngleCurved.svg`;
								} else {
									return `${imagePath}Angle.svg`;
								}
							case "stainless":
								return `${imagePath}AngleCurved.svg`;
							case "carbon":
								return `${imagePath}AngleCurved.svg`;
							default:
								return `${imagePath}Angle.svg`;
						}
					case "beam":
						switch (category?.toLowerCase()) {
							case "aluminum":
								if (productType?.value?.toLowerCase() === "american std") {
									return `${imagePath}BeamAngle.svg`;
								} else {
									return `${imagePath}Beam.svg`;
								}
							case "stainless":
								return `${imagePath}Beam.svg`;
							case "carbon":
								switch (productType?.value?.toLowerCase()) {
									case "american std":
										return `${imagePath}BeamAngle.svg`;
									case "standard":
										return `${imagePath}BeamRound.svg`;
									default:
										return `${imagePath}Beam.svg`;
								}
							default:
								return `${imagePath}Beam.svg`;
						}
					case "channel":
						switch (category?.toLowerCase()) {
							case "aluminum":
								switch (productType?.value?.toLowerCase()) {
									case "alum assoc":
										return `${imagePath}ChannelCurved.svg`;
									case "american std":
										return `${imagePath}ChannelCurvedTeeSide.svg`;
									default:
										return `${imagePath}Channel.svg`;
								}
							case "stainless":
								return `${imagePath}ChannelCurved.svg`;
							case "carbon":
								return `${imagePath}ChannelCurvedTeeSide.svg`;
							default:
								return `${imagePath}Channel.svg`;
						}
					case "tee":
						return `${imagePath}BarTee.svg`;
					default:
						return defaultImage;
				}
			case "tread plate":
				return `${imagePath}PlateRectangleTread.svg`;
			case "tube":
				switch (shape?.toLowerCase()) {
					case "rectangle":
						return `${imagePath}TubeRectangle.svg`;
					case "round":
						return `${imagePath}TubeRound.svg`;
					case "square":
						return `${imagePath}TubeSquare.svg`;
					default:
						return `${imagePath}TubeRound.svg`;
				}
			default:
				return defaultImage;
		}
	}
};
